//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React, { useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import { Button, Dropdown, DropdownProps, Label, Message, Modal, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { Change } from "../../api/changelog/v1/ichangelog";
import { NotificationRuleTarget as ApiNotificationRuleTarget } from "../../api/dashboard/v1/idashboard";
import { FormActionButtonCancel, UserLink } from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import {
  DerivedRuleChange,
  NotificationRuleTarget,
  RULE_URL_PREFIX,
  RuleChange,
  RuleChangeAction,
  actionLabels,
  deriveRuleChanges,
  lastUrlSegment,
  removalChangeFromChange,
  ruleChangeFromChange,
  targetLabels,
} from "./types";

const targets: NotificationRuleTarget[] = ["deployment", "organization"];

const apiTargets: Record<NotificationRuleTarget, ApiNotificationRuleTarget> = {
  deployment: ApiNotificationRuleTarget.NOTIFICATION_RULE_TARGET_DEPLOYMENT,
  organization: ApiNotificationRuleTarget.NOTIFICATION_RULE_TARGET_ORGANIZATION,
};

const changesPageSize = 500;

// loadRuleChanges reads the changelog of notification rules and of "remove everywhere" actions (every page
// of ListNotificationRuleChanges per target and kind) and returns them derived and sorted newest first.
// Only targets the caller may list are queried: the proxy checks the rule-list permission per target.
export const loadRuleChanges = async (allowed: NotificationRuleTarget[] = targets): Promise<{ changes: DerivedRuleChange[]; errors: string[] }> => {
  const list = async (target: NotificationRuleTarget, removals: boolean) => {
    const items: Change[] = [];
    for (let page = 0; ; page++) {
      const resp = await apiClients.idashboardClient.ListNotificationRuleChanges({
        target: apiTargets[target],
        removals,
        options: { page, page_size: changesPageSize },
      });
      const pageItems = resp.items || [];
      items.push(...pageItems);
      if (pageItems.length < changesPageSize) return { items };
    }
  };
  const [ruleResults, removalResults] = await Promise.all([
    Promise.allSettled(allowed.map((t) => list(t, false))),
    Promise.allSettled(allowed.map((t) => list(t, true))),
  ]);
  const errors: string[] = [];
  const ruleChanges: RuleChange[] = [];
  const removals: DerivedRuleChange[] = [];
  allowed.forEach((target, i) => {
    const r = ruleResults[i];
    if (r.status === "fulfilled") {
      (r.value.items || []).forEach((c) => {
        try {
          ruleChanges.push(ruleChangeFromChange(c, target));
        } catch (e) {
          errors.push(`${target} rule change ${c.id}: ${e}`);
        }
      });
    } else {
      errors.push(`${target} rule changes: ${r.reason}`);
    }
    const m = removalResults[i];
    if (m.status === "fulfilled") {
      removals.push(...(m.value.items || []).map((c) => removalChangeFromChange(c, target)));
    } else {
      errors.push(`${target} removals: ${m.reason}`);
    }
  });
  const changes = [...deriveRuleChanges(ruleChanges), ...removals].sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
  return { changes, errors };
};

// ruleUrl is the URL of the rule a change belongs to (the value of the rule filter).
const ruleUrl = (c: RuleChange) => `${RULE_URL_PREFIX[c.target]}${encodeURIComponent(c.rule_id)}`;

const actionColor = (action: RuleChangeAction) => {
  switch (action) {
    case "created":
      return "green";
    case "deleted":
    case "removed_everywhere":
      return "red";
    case "disabled":
      return "orange";
    case "enabled":
      return "teal";
    default:
      return "blue";
  }
};

const DiffModal = ({ change, onClose }: { change: DerivedRuleChange; onClose: () => void }) => {
  const isRemoval = change.action === "removed_everywhere";
  const oldValue = !isRemoval && change.previous ? JSON.stringify(change.previous.rule, null, 2) : "";
  // A removal has no rule snapshot; show its recorded payload.
  const newValue = isRemoval ? JSON.stringify(change.payload, null, 2) : change.removed ? "" : JSON.stringify(change.rule, null, 2);
  return (
    <Modal open size="fullscreen">
      <Modal.Header>
        {actionLabels[change.action]} <code>{change.notification_id}</code> by {change.created_by_id ? <UserLink id={change.created_by_id} /> : "-"}
      </Modal.Header>
      <Modal.Content scrolling>
        <ReactDiffViewer splitView oldValue={oldValue} newValue={newValue} compareMethod={DiffMethod.WORDS} />
      </Modal.Content>
      <Modal.Actions>
        <FormActionButtonCancel title="Close" onClick={onClose} />
      </Modal.Actions>
    </Modal>
  );
};

// RuleAuditLogView lists who created, edited, enabled, disabled or deleted notification rules and who
// removed a notification everywhere. Entries come from changelogd (see loadRuleChanges).
// ruleFilter is a rule URL (or "all").
export const RuleAuditLogView = ({
  changes,
  ruleFilter,
  onRuleFilterChange,
}: {
  changes: DerivedRuleChange[];
  ruleFilter: string | "all";
  onRuleFilterChange: (ruleUrl: string | "all") => void;
}) => {
  const [actionFilter, setActionFilter] = useState<RuleChangeAction | "all">("all");
  const [targetFilter, setTargetFilter] = useState<NotificationRuleTarget | "all">("all");
  const [diff, setDiff] = useState<DerivedRuleChange | undefined>(undefined);

  const derived = changes;
  // Rules known from the changelog, including deleted ones (newest change per rule decides the label).
  const ruleEntries = new Map<string, DerivedRuleChange>();
  derived.filter((c) => c.action !== "removed_everywhere").forEach((c) => !ruleEntries.has(ruleUrl(c)) && ruleEntries.set(ruleUrl(c), c));
  const ruleOptions = [
    { key: "all", value: "all", text: "Rule: All" },
    ...Array.from(ruleEntries.entries()).map(([url, c]) => ({
      key: url,
      value: url,
      text: `Rule: ${c.notification_id || c.rule_id}${c.action === "deleted" ? " (deleted)" : ""}`,
    })),
  ];
  const ruleFilterId = ruleFilter === "all" ? "" : lastUrlSegment(ruleFilter);
  const actionOptions = [
    { key: "all", value: "all", text: "Action: All" },
    ...(Object.keys(actionLabels) as RuleChangeAction[]).map((key) => ({ key, value: key, text: `Action: ${actionLabels[key]}` })),
  ];
  const targetOptions = [
    { key: "all", value: "all", text: "Target: All" },
    ...(Object.keys(targetLabels) as NotificationRuleTarget[]).map((key) => ({ key, value: key, text: `Target: ${targetLabels[key]}` })),
  ];
  const visible = derived.filter(
    (c) =>
      (ruleFilter === "all" || (c.action !== "removed_everywhere" && c.rule_id === ruleFilterId && ruleFilter.startsWith(RULE_URL_PREFIX[c.target]))) &&
      (actionFilter === "all" || c.action === actionFilter) &&
      (targetFilter === "all" || c.target === targetFilter)
  );

  return (
    <>
      {diff && <DiffModal change={diff} onClose={() => setDiff(undefined)} />}
      <Dropdown
        selection
        options={ruleOptions}
        value={ruleFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => onRuleFilterChange(data.value as string)}
      />{" "}
      <Dropdown
        selection
        options={actionOptions}
        value={actionFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setActionFilter(data.value as RuleChangeAction | "all")}
      />{" "}
      <Dropdown
        selection
        options={targetOptions}
        value={targetFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setTargetFilter(data.value as NotificationRuleTarget | "all")}
      />
      <Table striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>When</Table.HeaderCell>
            <Table.HeaderCell>Who</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
            <Table.HeaderCell>Target</Table.HeaderCell>
            <Table.HeaderCell>Rule</Table.HeaderCell>
            <Table.HeaderCell>Changes</Table.HeaderCell>
            <Table.HeaderCell>Details</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visible.map((c) => (
            <Table.Row key={c.id}>
              <Table.Cell>
                <DateTimePopupWithUTCAndLocalTime dateTime={c.created_at.toISOString()} label="Changed at" />
              </Table.Cell>
              <Table.Cell>{c.created_by_id ? <UserLink id={c.created_by_id} /> : "-"}</Table.Cell>
              <Table.Cell>
                <Label size="tiny" color={actionColor(c.action)}>
                  {actionLabels[c.action]}
                </Label>
              </Table.Cell>
              <Table.Cell>{targetLabels[c.target]}</Table.Cell>
              <Table.Cell>
                <code>{c.notification_id}</code>
                {c.rule_id && (
                  <>
                    <br />
                    <small>{c.rule_id}</small>
                  </>
                )}
              </Table.Cell>
              <Table.Cell>
                {c.summary.length ? (
                  <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                    {c.summary.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  "-"
                )}
              </Table.Cell>
              <Table.Cell>
                <Button basic size="tiny" icon="exchange" content="Diff" onClick={() => setDiff(c)} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {!visible.length && <Message>No changes recorded</Message>}
    </>
  );
};
